import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { useAuth } from "@/hooks/useAuth";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
// UPPDRAG-34: Users icon for the new "players" feature flag — same icon
// the player-side DesktopSidebar uses for /players, so the toggle's
// affordance matches the gated surface.
import { Loader2, CalendarDays, Clock, Repeat, Trophy, GraduationCap, Dumbbell, MessageCircle, MessageSquare, Crown, Wallet, Coins, FileText, Baby, Package, CheckSquare, Tv, BarChart3, Sparkles, Users } from "lucide-react";
import { UserPlus } from "lucide-react";
import SportIcon from "@/components/SportIcon";
import OpenMatchIcon from "@/components/icons/OpenMatchIcon";

const featureGroups = [
  {
    labelKey: "settings.features.groupBooking",
    features: [
      { key: "bookings", labelKey: "settings.features.bookings", descKey: "settings.features.bookingsDesc", icon: CalendarDays, locked: true },
      { key: "open_matches", labelKey: "settings.features.openMatches", descKey: "settings.features.openMatchesDesc", icon: OpenMatchIcon },
      { key: "tournaments", labelKey: "settings.features.tournaments", descKey: "settings.features.tournamentsDesc", icon: ({ className }: { className?: string }) => <SportIcon sport="padel" size="sm" className={className} /> },
      { key: "waitlist", labelKey: "settings.features.waitlist", descKey: "settings.features.waitlistDesc", icon: Clock },
      { key: "recurring", labelKey: "settings.features.recurring", descKey: "settings.features.recurringDesc", icon: Repeat },
      { key: "player_activities", labelKey: "settings.features.playerActivities", descKey: "settings.features.playerActivitiesDesc", icon: Sparkles },
    ],
  },
  {
    labelKey: "settings.features.groupActivities",
    features: [
      { key: "leagues", labelKey: "settings.features.leagues", descKey: "settings.features.leaguesDesc", icon: Trophy },
      { key: "training", labelKey: "settings.features.training", descKey: "settings.features.trainingDesc", icon: GraduationCap },
      { key: "coaching", labelKey: "settings.features.coaching", descKey: "settings.features.coachingDesc", icon: Dumbbell },
    ],
  },
  {
    labelKey: "settings.features.groupCommunity",
    features: [
      // UPPDRAG-34: "players" gates /players + /players/:id (player
      // discovery in DesktopSidebar). Default-on (preserves pre-flag
      // behaviour); halls that don't want the directory exposed can
      // toggle it off.
      // UPPDRAG-58: subFlags renders nested sub-toggles inside the same
      // card, indented + gated on the parent flag being on (a
      // disabled parent makes the children moot). Only one sub-flag
      // today (players_ranking), but the shape supports more.
      {
        key: "players",
        labelKey: "settings.features.players",
        descKey: "settings.features.playersDesc",
        icon: Users,
        subFlags: [
          { key: "players_ranking", labelKey: "settings.features.playersRanking", descKey: "settings.features.playersRankingDesc" },
        ],
      },
      { key: "community", labelKey: "settings.features.community", descKey: "settings.features.communityDesc", icon: MessageCircle },
      { key: "chat", labelKey: "settings.features.chat", descKey: "settings.features.chatDesc", icon: MessageSquare },
    ],
  },
  {
    labelKey: "settings.features.groupPayment",
    features: [
      { key: "memberships", labelKey: "settings.features.memberships", descKey: "settings.features.membershipsDesc", icon: Crown },
      { key: "credits", labelKey: "settings.features.credits", descKey: "settings.features.creditsDesc", icon: Wallet },
      { key: "rewards", labelKey: "settings.features.rewards", descKey: "settings.features.rewardsDesc", icon: Coins },
      { key: "invoicing", labelKey: "settings.features.invoicing", descKey: "settings.features.invoicingDesc", icon: FileText },
    ],
  },
  {
    labelKey: "settings.features.groupOther",
    features: [
      { key: "junior_accounts", labelKey: "settings.features.juniorAccounts", descKey: "settings.features.juniorAccountsDesc", icon: Baby },
      { key: "addons", labelKey: "settings.features.addons", descKey: "settings.features.addonsDesc", icon: Package },
      { key: "checkin", labelKey: "settings.features.checkin", descKey: "settings.features.checkinDesc", icon: CheckSquare },
      { key: "tv_display", labelKey: "settings.features.tvDisplay", descKey: "settings.features.tvDisplayDesc", icon: Tv },
      { key: "stats", labelKey: "settings.features.stats", descKey: "settings.features.statsDesc", icon: BarChart3, locked: true },
    ],
  },
];

export default function SettingsFeatures() {
  const { t } = useTranslation("admin");
  const { organization } = useOrganization();
  // UPPDRAG-32: feed configured_by into the audit log so admins can see
  // who toggled what when (the audit-diff trigger inserts a row per
  // changed boolean column with this user as changed_by).
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: flags, isLoading } = useQuery({
    queryKey: ["feature-flags", organization?.id],
    queryFn: async () => {
      const { data } = await supabase
        .from("feature_flags")
        .select("*")
        .eq("organization_id", organization!.id)
        .maybeSingle();
      return data;
    },
    enabled: !!organization?.id,
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: boolean }) => {
      // UPPDRAG-32: configured_by is read by tg_feature_flags_audit_diff
      // to populate feature_flag_audit_log.changed_by. The trigger picks
      // up NEW.configured_by, so we set it on every UPDATE/INSERT.
      const configured_by = user?.id ?? null;
      if (!flags?.id) {
        // Create if not exists. The org-creation trigger (UPPDRAG-32)
        // means a row should usually already exist, but this is the
        // safety net for orgs created before the migration.
        const { error } = await supabase.from("feature_flags").insert({
          organization_id: organization!.id,
          [`${key}_enabled`]: value,
          configured_by,
        } as any);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("feature_flags")
          .update({ [`${key}_enabled`]: value, configured_by } as any)
          .eq("id", flags.id);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["feature-flags"] });
      toast.success(t("admin:toast.featureUpdated"));
    },
    onError: (err: Error) => { console.error(err); toast.error(err?.message || t("settings.features.errorUpdate")); },
  });

  if (isLoading) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const isOn = (key: string) => {
    if (!flags) return false;
    return (flags as any)[`${key}_enabled`] ?? false;
  };

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold mb-1">{t("settings.features.title")}</h3>
        <p className="text-sm text-muted-foreground mb-6">{t("settings.features.description")}</p>
      </div>

      {featureGroups.map((group) => (
        <div key={group.labelKey}>
          <p className="text-[10px] font-semibold tracking-widest text-muted-foreground/60 uppercase mb-3">{t(group.labelKey)}</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {group.features.map((feat) => {
              const { key, labelKey, descKey, icon: Icon, locked } = feat as typeof feat & { subFlags?: Array<{ key: string; labelKey: string; descKey: string }> };
              const subFlags = (feat as { subFlags?: Array<{ key: string; labelKey: string; descKey: string }> }).subFlags;
              const parentOn = locked ? true : isOn(key);
              return (
                <div
                  key={key}
                  className="flex items-start gap-3 rounded-lg border p-4 transition-colors hover:bg-muted/50"
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <Icon className="h-4.5 w-4.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium">{t(labelKey)}</span>
                      <Switch
                        checked={parentOn}
                        disabled={locked || toggleMutation.isPending}
                        onCheckedChange={(v) => toggleMutation.mutate({ key, value: v })}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{t(descKey)}</p>

                    {/* UPPDRAG-58: nested sub-toggles. Indented + only
                        visible while the parent flag is on (children
                        are functionally moot otherwise). Default-on
                        gate (`!== false`) mirrors the consumer pattern
                        in Players.tsx — a missing/null DB value falls
                        back to "on", so a never-toggled flag behaves
                        the same as the migration default. */}
                    {subFlags && parentOn && (
                      <div className="mt-3 space-y-2 border-l-2 border-muted pl-3">
                        {subFlags.map((sub) => {
                          const subOn = (flags as Record<string, unknown> | null | undefined)?.[`${sub.key}_enabled`] !== false;
                          return (
                            <div key={sub.key} className="flex items-start justify-between gap-2">
                              <div className="min-w-0 flex-1">
                                <span className="text-xs font-medium">{t(sub.labelKey)}</span>
                                <p className="text-[11px] text-muted-foreground mt-0.5">{t(sub.descKey)}</p>
                              </div>
                              <Switch
                                checked={subOn}
                                disabled={toggleMutation.isPending}
                                onCheckedChange={(v) => toggleMutation.mutate({ key: sub.key, value: v })}
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Public signup toggle */}
      <div>
        <p className="text-[10px] font-semibold tracking-widest text-muted-foreground/60 uppercase mb-3">{t("settings.features.groupRegistration")}</p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex items-start gap-3 rounded-lg border p-4 transition-colors hover:bg-muted/50">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
              <UserPlus className="h-4.5 w-4.5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium">{t("settings.features.publicSignup")}</span>
                <Switch
                  checked={(flags as any)?.public_signup_enabled ?? false}
                  disabled={toggleMutation.isPending}
                  onCheckedChange={(v) => toggleMutation.mutate({ key: "public_signup", value: v })}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">{t("settings.features.publicSignupDesc")}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-dashed p-4 bg-muted/30">
        <p className="text-sm font-medium mb-1">{t("settings.features.saasplan")}: {flags?.plan === "enterprise" ? "Enterprise" : flags?.plan === "professional" ? "Professional" : "Starter"}</p>
        <p className="text-xs text-muted-foreground">{t("settings.features.planLimitHint")}</p>
      </div>
    </div>
  );
}
